import React, { useState, useEffect } from 'react';
import type { Subscription, BillingCycle, SubscriptionStatus, SubscriptionCategory } from '../types/subscription';
import { createSubscription, updateSubscription } from '../services/subscriptionService';
import { SERVICE_PRESETS, getPreset } from '../config/servicePresets';
import { X, DollarSign, Calendar, AlignLeft, ChevronDown } from 'lucide-react';
import { APP_CURRENCY } from '../config/currency';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingSub?: Subscription | null;
}

const CYCLES: { label: string; value: BillingCycle }[] = [
  { label: 'Weekly',    value: 'weekly' },
  { label: 'Monthly',  value: 'monthly' },
  { label: 'Quarterly',value: 'quarterly' },
  { label: 'Yearly',   value: 'yearly' },
];

const STATUSES: { label: string; value: SubscriptionStatus }[] = [
  { label: 'Active',    value: 'active' },
  { label: 'Trial',     value: 'trial' },
  { label: 'Paused',    value: 'paused' },
  { label: 'Cancelled', value: 'cancelled' },
];

const CATEGORIES: { label: string; value: SubscriptionCategory }[] = [
  { label: 'Productivity',  value: 'productivity' },
  { label: 'Development',   value: 'development' },
  { label: 'Cloud',         value: 'cloud' },
  { label: 'Entertainment', value: 'entertainment' },
  { label: 'Communication', value: 'communication' },
  { label: 'Design',        value: 'design' },
  { label: 'Finance',       value: 'finance' },
  { label: 'Education',     value: 'education' },
  { label: 'Other',         value: 'other' },
];

const CURRENCIES = [APP_CURRENCY];

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen, onClose, onSaved, editingSub,
}) => {
  const [logoKey,      setLogoKey]      = useState('');
  const [customName,   setCustomName]   = useState('');
  const [amount,       setAmount]       = useState('');
  const [currency,     setCurrency]     = useState(APP_CURRENCY);
  const [cycle,        setCycle]        = useState<BillingCycle>('monthly');
  const [status,       setStatus]       = useState<SubscriptionStatus>('active');
  const [category,     setCategory]     = useState<SubscriptionCategory>('other');
  const [nextDate,     setNextDate]     = useState('');
  const [startedDate,  setStartedDate]  = useState('');
  const [notes,        setNotes]        = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [showPicker,   setShowPicker]   = useState(false);
  const [loading,      setLoading]      = useState(false);

  useEffect(() => {
    if (editingSub) {
      setLogoKey(editingSub.logoKey || '');
      setCustomName(editingSub.customName || editingSub.serviceName || '');
      setAmount(String(editingSub.amount));
      setCurrency(editingSub.currency);
      setCycle(editingSub.billingCycle);
      setStatus(editingSub.status);
      setCategory(editingSub.category);
      setNextDate(editingSub.nextBillingDate || '');
      setStartedDate(editingSub.startedDate || '');
      setNotes(editingSub.notes || '');
    } else {
      setLogoKey(''); setCustomName(''); setAmount(''); setCurrency(APP_CURRENCY);
      setCycle('monthly'); setStatus('active'); setCategory('other');
      setNextDate(''); setStartedDate(''); setNotes('');
    }
    setServiceSearch('');
    setShowPicker(false);
  }, [editingSub, isOpen]);

  if (!isOpen) return null;

  const selectedPreset = getPreset(logoKey);
  const displayName = customName || selectedPreset?.name || 'Service';

  const handlePickPreset = (key: string) => {
    const preset = getPreset(key);
    setLogoKey(key);
    if (preset && !customName) {
      setCustomName(preset.name);
      setCategory(preset.category);
    }
    setShowPicker(false);
  };

  const filteredPresets = SERVICE_PRESETS.filter((p) =>
    !serviceSearch || p.name.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = Number(amount);
    if (!customName.trim() || !num || num <= 0) return;
    setLoading(true);
    const data = {
      serviceName: customName.trim(),
      customName: customName.trim(),
      logoKey: logoKey || undefined,
      category,
      amount: num,
      currency,
      billingCycle: cycle,
      status,
      nextBillingDate: nextDate || undefined,
      startedDate: startedDate || undefined,
      notes: notes.trim() || undefined,
    };
    if (editingSub) await updateSubscription(editingSub.id, data);
    else await createSubscription(data);
    setLoading(false);
    onSaved();
    onClose();
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 bg-white/5 border border-white/10 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl shadow-2xl fade-in-up overflow-hidden"
        style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h2 className="text-base font-semibold text-white">
            {editingSub ? 'Edit Subscription' : 'Add Subscription'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">

            {/* Service picker */}
            <div>
              <label className={labelClass}>Service</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPicker(!showPicker)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                >
                  {selectedPreset ? (
                    <span
                      className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base"
                      style={{ background: selectedPreset.color }}
                    >
                      {selectedPreset.emoji}
                    </span>
                  ) : (
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-500 text-base">📦</span>
                  )}
                  <span className="text-sm text-white flex-1 text-left">{displayName}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
                </button>

                {showPicker && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-2xl z-10"
                    style={{ background: '#0f0f19', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <div className="p-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <input
                        autoFocus
                        className="w-full px-3 py-2 rounded-lg bg-white/5 text-sm text-white placeholder-gray-500 border border-white/10 focus:outline-none focus:border-indigo-500"
                        placeholder="Search services…"
                        value={serviceSearch}
                        onChange={(e) => setServiceSearch(e.target.value)}
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto p-2 grid grid-cols-2 gap-1">
                      {filteredPresets.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => handlePickPreset(p.key)}
                          className={`flex items-center gap-2 px-2 py-2 rounded-lg text-left hover:bg-white/5 transition-colors ${logoKey === p.key ? 'bg-indigo-500/20' : ''}`}
                        >
                          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0" style={{ background: p.color }}>
                            {p.emoji}
                          </span>
                          <span className="text-xs text-gray-300 truncate">{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Custom name */}
            <div>
              <label className={labelClass}>Display Name</label>
              <input
                id="subServiceName"
                className={inputClass}
                placeholder="e.g. Cursor Pro"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                required
              />
            </div>

            {/* Amount + Currency + Cycle */}
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-2">
                <label className={labelClass}>Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    id="subAmount"
                    className={`${inputClass} pl-9`}
                    type="number" step="0.01" min="0.01" placeholder="0.00"
                    value={amount} onChange={(e) => setAmount(e.target.value)} required
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Currency</label>
                <select className={`${inputClass} appearance-none`} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c} className="bg-gray-800">{c}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Billing Cycle</label>
                <select className={`${inputClass} appearance-none`} value={cycle} onChange={(e) => setCycle(e.target.value as BillingCycle)}>
                  {CYCLES.map((c) => <option key={c.value} value={c.value} className="bg-gray-800">{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Status + Category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Status</label>
                <select className={`${inputClass} appearance-none`} value={status} onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}>
                  {STATUSES.map((s) => <option key={s.value} value={s.value} className="bg-gray-800">{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Category</label>
                <select className={`${inputClass} appearance-none`} value={category} onChange={(e) => setCategory(e.target.value as SubscriptionCategory)}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value} className="bg-gray-800">{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  <Calendar className="inline h-3 w-3 mr-1" />Next Billing Date
                </label>
                <input className={inputClass} type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Started Date <span className="text-gray-600">(optional)</span></label>
                <input className={inputClass} type="date" value={startedDate} onChange={(e) => setStartedDate(e.target.value)} />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className={labelClass}>
                <AlignLeft className="inline h-3 w-3 mr-1" />Notes <span className="text-gray-600">(optional)</span>
              </label>
              <textarea
                className={`${inputClass} resize-none`} rows={2}
                placeholder="Plan name, account email, etc."
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 pb-6">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors shadow-lg shadow-indigo-500/25">
              {loading ? 'Saving…' : editingSub ? 'Update' : 'Add Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;
